import React, { useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RootState } from '@@/store';
import {
  useGetDocumentQuery,
  usePatchDocumentMutation,
} from '@@/services/document';
import { hideDocumentEditionModal } from '@@/features/modal';
import DocumentEditionForm, {
  schema,
} from '@@/components/Forms/DocumentEditionForm';

const DocumentEditionModal: React.FC<{
  documentId: string;
  onClose: () => void;
}> = ({ documentId, onClose }) => {
  const dispatch = useDispatch();
  const showDocumentEditionModal = useSelector(
    (state: RootState) => state.modal.showDocumentEditionModal,
  );
  const { data: document } = useGetDocumentQuery(documentId || '', {
    skip: !documentId || !showDocumentEditionModal,
  });

  const [patchDocument, { isSuccess }] = usePatchDocumentMutation();

  const form = useForm<Unpatient.Document>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const onSubmit = useCallback(
    (data: Unpatient.Document) => {
      if (documentId) {
        patchDocument({
          isPrivate: !!data.isPrivate,
          label: data.label,
          type: data.type,
          documentCreationDate: data.documentCreationDate,
          documentId,
        });
      }
    },
    [patchDocument, documentId],
  );

  const closeModal = useCallback(() => {
    onClose();

    dispatch(hideDocumentEditionModal());
  }, [dispatch, onClose]);

  useEffect(() => {
    if (document?.id) {
      form.reset({
        label: document.label || document.name,
        type: document.type,
        isPrivate: document.isPrivate,
        documentCreationDate: document.documentCreationDate,
      });
    }
  }, [document?.id, form.reset]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Le document a bien été modifié`, {
        position: 'top-right',
      });
      closeModal();
    }
  }, [isSuccess, closeModal]);

  return (
    <Modal
      isOpen={showDocumentEditionModal}
      onRequestClose={closeModal}
      className="absolute bg-white mt-4 py-6 px-8 border border-gray-300 rounded-lg top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] z-90"
      contentLabel="Show document edition modal"
      ariaHideApp={false}
    >
      <DocumentEditionForm form={form} />

      <button
        type="button"
        className="mt-2 py-2 px-3 bg-sky-600 text-white rounded-xl font-main text-base w-fit"
        onClick={form.handleSubmit(onSubmit)}
        disabled={form.formState.isSubmitting}
      >
        <span className="ml-2">Valider</span>
      </button>
    </Modal>
  );
};

export default DocumentEditionModal;
